import { CommonModule } from '@angular/common';
import { Component, computed, Inject, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import {
  MessagingData,
  MessagingResendData,
} from '@features/order-detail/models/order-messaging.model';
import { OrderMessagingService } from '@features/order-detail/services/order-messaging.service';
import { injectQuery, queryOptions } from '@ngneat/query';
import { HorusLoadingComponent } from '@shared/components/domain/horus-loading/horus-loading.component';
import { HorusMessageGeneralComponent } from '@shared/components/domain/horus-message-general/horus-message-general.component';
import { MessageTypeLabelComponent } from '@shared/components/domain/message-type-label/message-type-label.component';
import { RouteStateService } from 'app/core/services/route-state.service';
import { DialogManagerService } from 'app/shared/services/dialog-manager.service';
import { derivedAsync } from 'ngxtension/derived-async';
import { HorusBtnDirective } from '../../../../shared/directives/horus-btn.directive';
import { OdMessagingResendDialogComponent } from '../od-messaging-resend-dialog/od-messaging-resend-dialog.component';

@Component({
  selector: 'app-od-messaging-timeline-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    HorusLoadingComponent,
    HorusMessageGeneralComponent,
    MessageTypeLabelComponent,
    HorusBtnDirective,
  ],
  templateUrl: './od-messaging-timeline-dialog.component.html',
  styleUrl: './od-messaging-timeline-dialog.component.scss',
})
export class OdMessagingTimelineDialogComponent {
  dialogRef = inject(MatDialogRef<OdMessagingTimelineDialogComponent>);

  private readonly messagingService = inject(OrderMessagingService);
  private readonly query = injectQuery();
  private readonly routeState = inject(RouteStateService);
  private readonly dialogManagerService = inject(DialogManagerService);

  // Convert data to signal for reactive queries
  readonly messagingData = signal<MessagingData | null>(null);

  constructor(@Inject(MAT_DIALOG_DATA) public data: MessagingData) {
    this.messagingData.set(data);
  }

  // Helper method for query options
  private getMessageDetailQueryOptions(tenantId: string, emailId: string) {
    return queryOptions({
      queryKey: ['messaging', 'message-detail', tenantId, emailId] as const,
      queryFn: () =>
        this.messagingService.fetchMessageDetail(tenantId, emailId),
    });
  }

  // Reactive query using derivedAsync
  readonly messageDetailQuery = derivedAsync(() => {
    const data = this.messagingData();
    if (!data) {
      return Promise.resolve(null);
    }

    return this.query({
      ...this.getMessageDetailQueryOptions(data.tenantId, data.emailId),
      enabled: !!data.tenantId && !!data.emailId,
    }).result$;
  });

  // Computed state selectors
  readonly isLoading = computed(
    () => this.messageDetailQuery()?.isLoading ?? false
  );
  readonly hasError = computed(
    () => this.messageDetailQuery()?.isError ?? false
  );
  readonly error = computed(() => this.messageDetailQuery()?.error ?? null);
  readonly messageDetail = computed(() => {
    const query = this.messageDetailQuery();
    const data = this.messagingData();

    if (!query?.data || !data) return [];

    return OrderMessagingService.buildTimelineSteps(
      query.data,
      data.messageType
    );
  });

  // Computed helpers for timeline step states
  readonly lastActiveIndex = computed(() => {
    const steps = this.messageDetail();
    for (let i = steps.length - 1; i >= 0; i--) {
      if (steps[i].createdAt) {
        return i;
      }
    }
    return -1;
  });

  readonly firstActiveIndex = computed(() => {
    const steps = this.messageDetail();
    for (let i = 0; i < steps.length; i++) {
      if (steps[i].createdAt) {
        return i;
      }
    }
    return -1;
  });

  isLastActive(index: number): boolean {
    return this.lastActiveIndex() === index;
  }

  isFirstActive(index: number): boolean {
    return this.firstActiveIndex() === index;
  }

  loadMessagingData(): void {
    // Refetch the message detail query
    const query = this.messageDetailQuery();
    if (query?.refetch) {
      query.refetch();
    }
  }

  openMessagingResendDialog(event: MouseEvent): void {
    const data = this.messagingData();
    if (!data) return;

    const resendData: MessagingResendData = {
      tenantId: this.routeState.getRouteParam('tenantId')?.toLowerCase()!,
      emailId: data.emailId,
      messageType: data.messageType,
      to: data.to,
    };

    this.dialogManagerService.openConfirm(
      OdMessagingResendDialogComponent,
      resendData,
      event,
      () => this.loadMessagingData()
    );
  }
}
